// @flow
import {type Node} from 'react'
import type {CalloutType} from 'components/ui/PremiumCallout'
import type {
  AdminSection_organization as Organization,
} from '../__generated__/AdminSection_organization.graphql'

type ConfigItem = {
  title: string,
  pathName: string,
  dataTurboFrame?: string,
  renderIcon?: () => Node,
  premiumCallout?: CalloutType,
  premiumCalloutLinkable?: boolean
}

type SectionItem = {
  title: string,
  href: string,
  dataTurboFrame?: string,
  renderIcon: ?() => Node,
  premiumCallout?: CalloutType,
  premiumCalloutLinkable?: boolean
}

class AdminLinks {
  static get adminLinksConfig(): ConfigItem[] {
    return [{
      title: I18n.t('side_nav.admin.organization_members'),
      pathName: 'adminOrganizationPeoplePath',
      dataTurboFrame: 'page-content-frame',
    }, {
      title: I18n.t('side_nav.admin.global_checklist'),
      pathName: 'adminOrganizationGlobalItemsPath',
      premiumCallout: 'admin_checklists_metrics',
    }, {
      title: I18n.t('side_nav.admin.holacracy_habits'),
      pathName: 'adminOrganizationHabitsPath',
      dataTurboFrame: 'page-content-frame',
      premiumCallout: 'admin_manage_habits',
    }, {
      title: I18n.t('side_nav.admin.organization_settings'),
      pathName: 'editAdminOrganizationPath',
      dataTurboFrame: 'page-content-frame',
    }, {
      title: I18n.t('side_nav.admin.billing_and_plans'),
      pathName: 'editAccountPath',
      dataTurboFrame: 'page-content-frame',
    }]
  }

  static get superUserLinksConfig(): ConfigItem[] {
    return [{
      title: 'Search People',
      pathName: 'adminSearchPeoplePath',
    }, {
      title: 'Search Organizations',
      pathName: 'adminSearchOrganizationPath',
    }, {
      title: 'Deactivated Organizations',
      pathName: 'deactivatedAdminOrganizationsPath',
    }, {
      title: 'Metrics Summary',
      pathName: 'metricsSummaryAdminOrganizationsPath',
    }, {
      title: 'Reports',
      pathName: 'adminReportsPath',
    }, {
      title: 'Lessons',
      pathName: 'adminLessonsPath',
    }, {
      title: 'Office Hours',
      pathName: 'adminOfficeHoursPath',
    }, {
      title: "What's New Announcements",
      pathName: 'announcementsPath',
    }, {
      title: 'Banner Notices',
      pathName: 'adminBannerNoticesPath',
    }, {
      title: 'AI Prompt Content',
      pathName: 'adminAiContentEditPath',
    }]
  }

  constructor(organization: Organization) {
    this.organization = organization
  }

  sectionItemParams: ((configItem: ConfigItem) => {
    href: string,
    renderIcon: ?(() => Node),
    title: string,
    dataTurboFrame?: string,
    premiumCallout?: CalloutType,
    premiumCalloutLinkable?: boolean
  })
    = (configItem: ConfigItem) => ({
      title: configItem.title,
      renderIcon: configItem.renderIcon,
      href: this.routes[configItem.pathName],
      dataTurboFrame: configItem.dataTurboFrame,
      premiumCallout: configItem.premiumCallout,
      premiumCalloutLinkable: configItem.premiumCalloutLinkable,
    })

  configureItems: ((items: Array<ConfigItem>) => Array<SectionItem>) = (items: ConfigItem[]): SectionItem[] => (
    items.map(this.sectionItemParams).filter((sectionItem) => !!sectionItem.href)
  )

  get sectionAdminItems(): SectionItem[] {
    return this.configureItems(AdminLinks.adminLinksConfig)
  }

  get sectionSuperAdminItems(): SectionItem[] {
    return this.isViewerSuperUser
      ? this.configureItems(AdminLinks.superUserLinksConfig)
      : []
  }

  // private

  get isViewerSuperUser(): boolean {
    return this.organization.viewerIsSuperUser
  }

  get routes(): {
  +adminBannerNoticesPath: ?string,
  +adminLessonsPath: ?string,
  +adminOfficeHoursPath: ?string,
  +adminOrganizationGlobalItemsPath: ?string,
  +adminOrganizationHabitsPath: ?string,
  +adminOrganizationPeoplePath: string,
  +adminReportsPath: ?string,
  +adminSearchOrganizationPath: ?string,
  +adminSearchPeoplePath: ?string,
  +announcementsPath: string,
  +deactivatedAdminOrganizationsPath: ?string,
  +editAccountPath: string,
  +editAdminOrganizationPath: string,
  +metricsSummaryAdminOrganizationsPath: ?string,
  +adminAiContentEditPath: ?string,
  } {
    return this.organization.viewer.routes
  }

  organization: Organization
}

export default AdminLinks
export type {Organization, SectionItem}
